<template>
	<div class="entity-history-button">
		<!-- Popup Menu with explicit activator -->
		<v-menu
			v-model="showPopup"
			location="left center"
			offset="16"
			:close-on-content-click="false"
			:open-on-click="false"
		>
			<template v-slot:activator="{ props }">
				<!-- Floating Button -->
				<v-btn
					v-bind="props"
					class="history-fab"
					color="#00aeef"
					icon
					size="56"
					elevation="6"
					@click.stop="togglePopup"
				>
					<v-icon size="24">mdi-history</v-icon>
				</v-btn>
			</template>

			<v-card class="history-popup" min-width="320">
				<v-card-title class="popup-header">
					<v-icon class="header-icon">mdi-history</v-icon>
					<span>{{ $t('text.viewChangesHistory') }}</span>
					<v-btn
						icon="mdi-close"
						size="small"
						variant="text"
						@click.stop="closePopup"
						class="close-btn"
					/>
				</v-card-title>

				<v-card-text class="popup-content">
					<!-- Quick Period Options -->
					<div class="section">
						<div class="section-title">{{ $t('text.quickOptions') }}</div>
						<div class="time-period-options">
							<v-btn
								v-for="option in timeOptions"
								:key="option.key"
								:loading="loading && selectedPeriod === option.key"
								:disabled="loading"
								variant="text"
								class="period-button"
								@click.stop="selectTimePeriod(option.key, option.days)"
							>
								<v-icon start size="20">{{ option.icon }}</v-icon>
								{{ option.label }}
							</v-btn>
						</div>
					</div>

					<!-- Custom Period Section -->
					<div class="section">
						<div class="section-title">{{ $t('text.numberOfDays') }}</div>
						<div class="custom-period">
							<v-text-field
								v-model.number="customDays"
								type="number"
								min="1"
								max="365"
								variant="solo"
								:disabled="loading"
								@keyup.enter="selectCustomPeriod"
								@click.stop
								class="days-input"
							>
								<template #append-inner>
									<v-btn
										:loading="loading && selectedPeriod === 'custom'"
										:disabled="loading || !customDays || customDays < 1"
										color="#00aeef"
										variant="outlined"
										@click.stop="selectCustomPeriod"
										class="apply-btn-inner"
										size="small"
									>
										{{ $t('text.apply') }}
									</v-btn>
								</template>
							</v-text-field>
						</div>
					</div>
				</v-card-text>
			</v-card>
		</v-menu>

		<!-- Generic changes display component passed via slot -->
		<slot 
			name="changes-display"
			:show="showChangesWizard"
			:changes="historyChanges"
			:title="wizardTitle"
			:entity-id="entityId"
			:entity-type="entityType"
			:close="closeChangesWizard"
		/>
	</div>
</template>

<script>
import AuditExecutive from '../../../../api2/src/modules/audit/AuditExecutive'
import { EntityType, getAllEntityTypes } from '../../../../api2/src/modules/audit/audit.types'

export default {
	name: 'EntityHistoryButton',
	props: {
		entityType: {
			type: String,
			required: true,
			validator: (value) => {
				// Validate against the enum values
				return getAllEntityTypes().includes(value)
			}
		},
		entityId: {
			type: String,
			required: true
		},
		// Optional executive override - falls back to AuditExecutive
		executive: {
			type: Object,
			default: null
		},
		// Method name to call on the executive for getting changes
		changesMethodName: {
			type: String,
			default: 'getEntityChangesSince'
		}
	},
	data() {
		return {
			showPopup: false,
			loading: false,
			selectedPeriod: null,
			customDays: 7,
			showChangesWizard: false,
			historyChanges: [],
			wizardTitle: '',
			timeOptions: [
				{
					key: 'week',
					days: 7,
					label: this.$t('text.lastWeek'),
					icon: 'mdi-calendar-week'
				},
				{
					key: 'month',
					days: 30,
					label: this.$t('text.lastMonth'),
					icon: 'mdi-calendar-month'
				},
				{
					key: 'quarter',
					days: 90,
					label: this.$t('text.last3Months'),
					icon: 'mdi-calendar-range'
				}
			]
		}
	},
	methods: {
		togglePopup() {
			console.log('togglePopup called, current state:', this.showPopup)
			this.showPopup = !this.showPopup
			console.log('togglePopup new state:', this.showPopup)
		},
		
		closePopup() {
			console.log('closePopup called')
			this.showPopup = false
			this.selectedPeriod = null
		},
		
		async selectTimePeriod(periodKey, days) {
			this.selectedPeriod = periodKey
			await this.loadChanges(days, this.getTimePeriodLabel(periodKey))
		},
		
		async selectCustomPeriod() {
			if (!this.customDays || this.customDays < 1) return
			
			this.selectedPeriod = 'custom'
			const label = this.$t('text.lastXDays', { days: this.customDays })
			await this.loadChanges(this.customDays, label)
		},
		
		async loadChanges(days, periodLabel) {
			this.loading = true
			try {
				// Use the provided executive or fall back to AuditExecutive
				const executive = this.executive || new AuditExecutive(this)
				
				// Calculate the date X days ago
				const sinceDate = new Date()
				sinceDate.setDate(sinceDate.getDate() - days)
				
				// Call the method dynamically based on the prop
				let result
				if (this.changesMethodName === 'getEntityChangesSince') {
					// Generic method expects entityType as first parameter
					result = await executive[this.changesMethodName](this.entityType, this.entityId, sinceDate)
				} else {
					// Legacy/specific methods expect entityId directly
					result = await executive[this.changesMethodName](this.entityId, sinceDate)
				}
				
				this.historyChanges = result.changes
				this.wizardTitle = this.$t('text.changesInPeriod', { period: periodLabel })
				
				// Close popup and show changes wizard
				this.closePopup()
				this.showChangesWizard = true
				
			} catch (error) {
				console.error(`Error loading ${this.entityType} changes:`, error)
				// Show error toast if available
				if (this.$parent.showErrorToast) {
					this.$parent.showErrorToast(this.$t('text.errorLoadingChanges'), true)
				}
			} finally {
				this.loading = false
			}
		},
		
		closeChangesWizard() {
			this.showChangesWizard = false
			this.historyChanges = []
			this.selectedPeriod = null
		},
		
		getTimePeriodLabel(periodKey) {
			const option = this.timeOptions.find(opt => opt.key === periodKey)
			return option ? option.label : periodKey
		}
	}
}
</script>

<style lang="scss" scoped>
.history-fab {
	position: fixed !important;
	bottom: 24px;
	right: 24px;
	z-index: 1000;
	box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
	
	&:hover {
		box-shadow: 0 6px 16px rgba(0, 0, 0, 0.2);
	}
}
	
.history-popup {
	border-radius: 12px;
	overflow: hidden;
	box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
}

.popup-header {
	background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
	padding: 20px 24px;
	display: flex;
	align-items: center;
	gap: 12px;
	border-bottom: 1px solid #dee2e6;
	
	.header-icon {
		color: #00aeef;
		font-size: 24px;
	}
	
	span {
		flex: 1;
		font-size: 18px;
		font-weight: 600;
		color: #343a40;
	}
	
	.close-btn {
		flex-shrink: 0;
		opacity: 0.7;
		
		&:hover {
			opacity: 1;
		}
	}
}

.popup-content {
	padding: 24px;
	
	.section {
		&:not(:last-child) {
			margin-bottom: 32px;
			padding-bottom: 24px;
			border-bottom: 1px solid #f1f3f4;
		}
		
		&:last-child {
			margin-bottom: 0;
		}
		
		.section-title {
			font-size: 14px;
			font-weight: 600;
			color: #6c757d;
			text-transform: uppercase;
			letter-spacing: 0.5px;
			margin-bottom: 16px;
		}
	}

}

.period-button {
	// margin-bottom: 12px;
}
.custom-period {
	display: flex;
	align-items:flex-end;
	gap: 16px;
	
	.days-input {
		flex: 1;
		
		:deep(.v-field) {
			border-radius: 10px;
		}
		
		:deep(.v-field__input) {
			padding: 12px 16px;
		}
	}
	
	.apply-btn-inner {
		height: 44px;
		min-width: 90px;
		font-weight: 600;
		border-radius: 10px;
		// border: 1.5px solid #00aeef;
		border: 1.5px solid #eee;
		
		&:hover {
			background-color: rgba(0, 174, 239, 0.08);
		}
		
		&:disabled {
			opacity: 0.5;
			border-color: #dee2e6;
		}
	}
}

.time-period-options {
	display: flex;
	flex-direction: column;
	gap: 12px;
	
	.period-button {
		justify-content: flex-start;
		text-transform: none;
		font-weight: 500;
		height: 52px;
		border: 1.5px solid #e9ecef;
		color: #495057;
		border-radius: 10px;
		
		&:hover {
			border-color: #00aeef;
			background-color: rgba(0, 174, 239, 0.04);
		}
		
		&:disabled {
			opacity: 0.6;
		}
		
		.v-icon {
			margin-right: 12px;
			color: #6c757d;
		}
	}
}

// Responsive adjustments
@media (max-width: 768px) {
	.entity-history-button {
		.history-fab {
			bottom: 16px;
			right: 16px;
			width: 48px !important;
			height: 48px !important;
		}
		
		.history-popup {
			min-width: 280px;
			
			.popup-content {
				padding: 20px;
				
				.custom-period {
					flex-direction: column;
					align-items: stretch;
					gap: 8px;
					
					.apply-btn-inner {
						width: 100%;
					}
				}
			}
		}
	}
}
</style> 